import { apiRequest } from '@department-of-veterans-affairs/platform-utilities/api';
import { focusElement } from 'platform/utilities/ui';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import FormNavButtons from '~/platform/forms-system/src/js/components/FormNavButtons';
import {
  FacilitySearchResults,
  PER_PAGE,
} from '../components/search/FacilitySearchResults';
import { SearchControls } from '../components/search/SearchControls';
import { getHealthFacilityTitle } from '../config/helpers';
import {
  CHAPTER_3,
  URL,
  envApiUrl,
  getMockTestingFlagForAPI,
} from '../constants';
import { useSearchIdGuard } from '../hooks/useSearchIdGuard';
import {
  formatHealthFacilityName,
  mapHealthResult,
} from '../utils/facility-search';
import { getCoordinatesFromQuery } from '../utils/mapbox';
import { mockHealthFacilityResponse } from '../utils/mockData';

/** @typedef {import('../types/health-facilities').HealthFacilitySearchResponse} HealthFacilitySearchResponse */
/** @typedef {import('../types/facility-search').SearchStatus} SearchStatus */

/** @type {HealthFacilitySearchResponse} Before the first search, and when a location isn't recognized */
const EMPTY_RESULTS = {
  data: [],
  meta: {
    pagination: {
      currentPage: 1,
      prevPage: null,
      nextPage: null,
      totalPages: 0,
      totalEntries: 0,
    },
  },
  links: { self: '', first: '', prev: null, next: null, last: '' },
};

/** Empty submit, or Continue before any search has started */
const EMPTY_QUERY_ERROR =
  'Enter a city or postal code of the nearest VA medical center';
/** Continue after a search with nothing to select (loading, no results, or failed) */
const SEARCH_AGAIN_ERROR =
  'Search again by city or postal code of the nearest VA medical center';
/** "Use my location" failed for any reason */
const GEOLOCATION_ERROR =
  'We couldn’t find your location. Try searching instead.';

/** Results from the last completed search; all fields change together */
const INITIAL_RESULTS = {
  raw: EMPTY_RESULTS,
  query: '',
  coordinates: /** @type {number[]} */ ([]),
  pageNum: 1,
};

/**
 * @param {number[]} coordinates [longitude, latitude]
 * @param {number} pageNum
 */
const buildSearchUrl = ([longitude, latitude], pageNum) =>
  `${envApiUrl}${URL.GET_HEALTH_FACILITY}?type=health&lat=${latitude}&long=${longitude}&radius=100&page=${pageNum}&per_page=${PER_PAGE}`;

export function YourVAHealthFacilityPage({
  data,
  setFormData,
  goBack,
  goForward,
}) {
  const currentPath = useSelector(state => state.navigation.route.path);
  // Persist if a facility was already selected (including after resume)
  const [showCurrentSelection] = useState(!!data.yourHealthFacilityName);
  const [results, setResults] = useState(INITIAL_RESULTS);
  const [status, setStatus] = useState(/** @type {SearchStatus} */ ('idle'));
  // The message itself; empty string means no error
  const [searchInvalidError, setSearchInvalidError] = useState('');
  const [radioError, setRadioError] = useState('');
  const { startSearch } = useSearchIdGuard();

  // True once any search has started, including failed and still-loading ones. Relies on:
  // 1. A finished search always saves a non-empty query. Saved queries come from a
  //    non-empty submit, a place name from "Use my location" (never empty), or a page
  //    change (reuses the saved query). Empty submits and failed lookups never search.
  // 2. Only `runSearch` changes `status`, and `'error'` lasts until the next search starts.
  const hasSearched = status !== 'idle' || results.query !== '';

  /**
   * Only called by `runSearch`
   * @param {number[]} coordinates [longitude, latitude]
   * @param {number} newPageNum
   * @returns {Promise<HealthFacilitySearchResponse>}
   */
  const fetchFacilities = (coordinates, newPageNum) =>
    getMockTestingFlagForAPI()
      ? new Promise(resolve =>
          setTimeout(() => resolve(mockHealthFacilityResponse), 500),
        )
      : apiRequest(buildSearchUrl(coordinates, newPageNum));

  /**
   * The only entry point for searches (text, location, page change). Don't call
   * fetchFacilities directly. Geocoding failures are handled like API failures, and
   * no coordinates like no results. On failure, previous results stay visible and selectable.
   * Responses from searches that have since been replaced are ignored.
   * @param {() => number[] | Promise<number[]>} getCoordinates [longitude, latitude], or [] if unrecognized
   * @param {string} query Shown in the results text
   * @param {number} [newPageNum]
   */
  const runSearch = (getCoordinates, query, newPageNum = 1) => {
    const { isLatest } = startSearch();

    setStatus('searching');
    setRadioError('');

    return Promise.resolve()
      .then(getCoordinates)
      .then(coordinates => {
        if (!isLatest()) return undefined;
        if (!coordinates.length) {
          setResults({
            raw: EMPTY_RESULTS,
            query,
            coordinates: [],
            pageNum: 1,
          });
          setStatus('idle');
          return undefined;
        }
        return fetchFacilities(coordinates, newPageNum).then(raw => {
          if (isLatest()) {
            setResults({ raw, query, coordinates, pageNum: newPageNum });
            if (raw.data.length) setSearchInvalidError(''); // There's something to select now
            setStatus('idle');
          }
        });
      })
      .catch(() => {
        if (isLatest()) setStatus('error');
      });
  };

  /**
   * A failed lookup only shows an error; search state, results, and selection are unchanged
   * @param {{ coordinates: number[], placeName: string } | null} location
   *   From "Use my location": [longitude, latitude] and a place name, or `null` if it failed
   */
  const handleGeoLocate = location => {
    if (!location) {
      setSearchInvalidError(GEOLOCATION_ERROR);
      return undefined;
    }
    setSearchInvalidError('');
    return runSearch(() => location.coordinates, location.placeName);
  };

  /** @param {string} query Trimmed, possibly empty */
  const handleSubmit = query => {
    setSearchInvalidError(query ? '' : EMPTY_QUERY_ERROR);
    if (!query) return undefined;
    return runSearch(() => getCoordinatesFromQuery(query), query);
  };

  /** @param {number} newPageNum */
  const handlePageChange = newPageNum =>
    runSearch(() => results.coordinates, results.query, newPageNum);

  /** @param {string} id Radio value: the facility ID */
  const handleRadioSelect = id => {
    const facility = results.raw.data.find(f => f.id === id);
    setRadioError('');
    setFormData({
      ...data,
      yourHealthFacility: id,
      yourHealthFacilityName: facility
        ? formatHealthFacilityName(facility)
        : '',
    });
  };

  const handleContinue = () => {
    // A valid selection always continues, whatever the search state
    if (data.yourHealthFacility) {
      goForward(data);
      return;
    }

    // Nothing to select: never searched, loading (radio list hidden), no results, or the search failed
    if (status === 'searching' || !results.query || !results.raw.data.length) {
      setSearchInvalidError(
        hasSearched ? SEARCH_AGAIN_ERROR : EMPTY_QUERY_ERROR,
      );
      focusElement('#facility-search-input va-text-input');
      return;
    }

    setRadioError('Select a facility');
    focusElement('va-radio');
  };

  return (
    <>
      <h3>{getHealthFacilityTitle(data)}</h3>
      <div className="rjsf">
        <p className="vads-u-margin-top--3 vads-u-margin-bottom--2">
          {CHAPTER_3.YOUR_VA_HEALTH_FACILITY.DESCRIPTION}
        </p>
        <div className="facility-locator vads-u-margin-top--2">
          <SearchControls
            onGeoLocate={handleGeoLocate}
            onSubmit={handleSubmit}
            searchLabel="Search by city or postal code"
            error={searchInvalidError}
          />
          <div role="alert">
            {status === 'error' && (
              <va-alert status="error" class="vads-u-margin-y--2" uswds>
                <p className="vads-u-margin-y--0">
                  We’re having trouble finding facilities right now. Try
                  searching again or select “Finish this question later.”
                </p>
              </va-alert>
            )}
          </div>
          {showCurrentSelection && (
            <div id="facility-current-selection">
              <p>
                <strong>Your current selection:</strong>
              </p>
              <p>{data.yourHealthFacilityName}</p>
            </div>
          )}
          <FacilitySearchResults
            results={results.raw.data.map(mapHealthResult)}
            pageNum={results.pageNum}
            totalCount={results.raw.meta.pagination.totalEntries}
            onPageChange={handlePageChange}
            onRadioSelect={handleRadioSelect}
            radioLabel="Select a VA medical center"
            noResultsMessage={`We couldn’t find any results for ${results.query}. Check the city or postal code you entered and try again.`}
            selected={data.yourHealthFacility}
            resultsQuery={results.query}
            error={radioError}
            isSearching={status === 'searching'}
          />
        </div>

        {currentPath !== '/review-then-submit' && (
          <FormNavButtons goBack={goBack} goForward={handleContinue} />
        )}
      </div>
    </>
  );
}

YourVAHealthFacilityPage.propTypes = {
  data: PropTypes.object,
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  setFormData: PropTypes.func,
};
